import React, { useCallback, useContext, useEffect, useState } from "react";
import translate from "../i18n/Translator";
import DialogPopup from "../components/DialogPopup";
import { Button, Grid, Hidden } from "@material-ui/core";
import { AppContext } from "../context/AppContext";
import { ErrorSnackbar } from "../components/Snackbars";
import { Cfdi, CancelCfdiRequest, CancelReason, CANCEL_REASONS } from "../model/Cfdi";
import { cancelCfdi } from "../api/CfdiAPI";
import { getCfdis } from "../api/CustomerCfdiAPI";
import ValidatedInput, { InputRef, isValid } from "../components/ValidatedInput";

interface CancelCfdiPopupProps {
    cfdi: Cfdi;
    onClose(): any;
    onSuccess(): any;
}

export default function CancelCfdiPopup(props: CancelCfdiPopupProps) {
    const context = useContext(AppContext);
    const margin = "dense";
    const tenantId = context.session!.tenant!.id;
    const customerId = props.cfdi.metadata?.customer_id || "-";
    const cfdiId = props.cfdi.id;
    const reasonLabels = CANCEL_REASONS.map(el => translate(`cfdis.cancel.reason.${el}`) as string);

    const [cfdiIds, setCfdiIds] = useState<string[]>([]);
    const [cfdiLabels, setCfdiLabels] = useState<string[]>([]);
    const [request, setRequest] = useState<CancelCfdiRequest>({} as CancelCfdiRequest);
    const [validations, setValidations] = useState<any>({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();

    const load = useCallback(() => {
        getCfdis(tenantId, customerId, 0, 0, { search: "", type: "" }).then(cfdis => {
            const filtered = cfdis.items.filter(el => el.id !== cfdiId);
            setCfdiIds(filtered.map(el => el.id));
            setCfdiLabels(filtered.map(el => [el.serie, el.folio].filter(el => !!el)).map(arr => arr.join("-")));
        }).catch(error => {
            console.error(error);
        })
    }, [tenantId, customerId, cfdiId]);

    useEffect(() => {
        if (request.reason === "errores_con_relacion") {
            load();
        }
    }, [load, request.reason]);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        if (name === "reason" && value !== "errores_con_relacion" as CancelReason) {
            setRequest({ ...request, reason: value as CancelReason, substitute_uuid: undefined });
        } else {
            setRequest({ ...request, [name]: value });
        }

        validations[name] = inputRef;
        setValidations(validations);
    };

    const onConfirm = () => {
        if (!isValid(validations)) {
            return;
        }

        setSubmitting(true);
        cancelCfdi(tenantId, props.cfdi.id, request).then(() => {
            props.onSuccess();
        }).catch(error => {
            setSubmitting(false);
            setError(error.message);
        });
    };

    return (
        <DialogPopup open title={translate("cfdis.cancel.title")} maxWidth="sm"
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            disable={submitting}
            onClose={props.onClose}
            closeColor="default"
            closeText={translate("buttons.close") as string}
            button={context.isGranted("CfdisDelete") ?
                <Button onClick={onConfirm} variant="outlined" color="secondary" disabled={submitting}>
                    {translate("buttons.confirm")}
                </Button>
                : undefined}>
            <Grid container justify="space-between" alignItems="center">
                <Grid item xs={12}>
                    <form autoComplete="off" noValidate onSubmit={onConfirm} >
                        <Grid container justify="space-between" alignItems="center">
                            <Grid item xs={12}>
                                <ValidatedInput type="text" id="reason" name="reason"
                                    value={request.reason} label={translate("cfdis.cancel.reason.title") as string}
                                    required={true} disabled={submitting}
                                    options={CANCEL_REASONS} optionLabels={reasonLabels}
                                    margin={margin}
                                    onValueChanged={hasChanged} />
                                <Hidden xsUp={request.reason !== "errores_con_relacion"}>
                                    <ValidatedInput type="text" id="substitute_uuid" name="substitute_uuid"
                                        value={request.substitute_uuid} label={translate("cfdis.cancel.substitute_uuid") as string}
                                        options={cfdiIds} optionLabels={cfdiLabels}
                                        required={request.reason === "errores_con_relacion"} disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Hidden>
                            </Grid>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
            <ErrorSnackbar message={error} onClose={() => setError(undefined)} />
        </DialogPopup>
    );

}